/**
 * 试卷编辑器组件
 * 支持添加、编辑、删除、排序题目
 * 优化交互：编辑时表单就地显示，添加时在顶部显示
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  GripVertical,
  Edit3,
  Trash2,
  ChevronUp,
  ChevronDown,
  Save,
  X,
  Check,
  HelpCircle,
  ListChecks,
  ToggleLeft,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import type {
  ExamPaperWithQuestions,
  ExamQuestion,
  QuestionCreateInPaper,
} from '@/types/api';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import styles from './PaperEditor.module.css';

interface PaperEditorProps {
  paper: ExamPaperWithQuestions;
  onSave: (paper: ExamPaperWithQuestions) => void;
  onCancel: () => void;
}

// 题目类型配置
const QUESTION_TYPES = [
  { value: 'single', label: '单选题', icon: ListChecks, color: '#3b82f6' },
  { value: 'multiple', label: '多选题', icon: Check, color: '#8b5cf6' },
  { value: 'judgment', label: '判断题', icon: ToggleLeft, color: '#f59e0b' },
  { value: 'essay', label: '简答题', icon: MessageSquare, color: '#10b981' },
];

// 难度配置
const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: '简单', color: '#22c55e' },
  { value: 'medium', label: '中等', color: '#f59e0b' },
  { value: 'hard', label: '困难', color: '#ef4444' },
];

// 获取题目类型配置
const getQuestionTypeConfig = (type: string) => {
  return QUESTION_TYPES.find(t => t.value === type) || QUESTION_TYPES[0];
};

// 获取难度配置
const getDifficultyConfig = (difficulty: string | undefined) => {
  return DIFFICULTY_OPTIONS.find(d => d.value === difficulty) || DIFFICULTY_OPTIONS[1];
};

// 默认题目表单
const emptyQuestion: QuestionCreateInPaper = {
  question_type: 'single',
  question_content: '',
  options: { A: '', B: '', C: '', D: '' },
  correct_answer: 'A',
  difficulty: 'medium',
  score: 10,
};

export default function PaperEditor({ paper, onSave, onCancel }: PaperEditorProps) {
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const { user } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);

  const [questions, setQuestions] = useState<ExamQuestion[]>(paper.questions || []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [saving, setSaving] = useState(false);
  const [questionForm, setQuestionForm] = useState<QuestionCreateInPaper>(emptyQuestion);

  // 试卷基本信息编辑状态
  const [passScore, setPassScore] = useState(paper.pass_score);
  const [examDuration, setExamDuration] = useState(paper.exam_duration);

  // 当 paper 变化时同步更新本地状态
  useEffect(() => {
    setQuestions(paper.questions || []);
    setPassScore(paper.pass_score);
    setExamDuration(paper.exam_duration);
  }, [paper]);

  // 计算总分
  const totalScore = questions.reduce((sum, q) => sum + q.score, 0);

  // 移动题目
  const moveQuestion = useCallback((index: number, direction: 'up' | 'down') => {
    const newQuestions = [...questions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= questions.length) return;
    [newQuestions[index], newQuestions[targetIndex]] = [newQuestions[targetIndex], newQuestions[index]];
    setQuestions(newQuestions);
  }, [questions]);

  // 验证表单
  const validateForm = (): boolean => {
    if (!questionForm.question_content.trim()) {
      showToast('warning', '请输入题目内容');
      return false;
    }

    // 非简答题需要验证选项
    if (questionForm.question_type !== 'essay') {
      const options = questionForm.options as Record<string, string> | null;

      if (questionForm.question_type === 'judgment') {
        // 判断题验证
        if (!options?.A?.trim() || !options?.B?.trim()) {
          showToast('warning', '请填写判断选项');
          return false;
        }
      } else {
        // 单选/多选题验证
        const filledOptions = Object.entries(options || {})
          .filter(([, value]) => value?.trim())
          .map(([key]) => key);

        if (filledOptions.length < 2) {
          showToast('warning', '请至少填写两个选项');
          return false;
        }

        // 验证正确答案
        const correctAnswers = questionForm.correct_answer.split('');
        const invalidAnswers = correctAnswers.filter(a => !filledOptions.includes(a));
        if (invalidAnswers.length > 0) {
          showToast('warning', `正确答案 ${invalidAnswers.join(', ')} 对应的选项未填写`);
          return false;
        }
      }
    } else {
      // 简答题验证
      if (!questionForm.correct_answer.trim()) {
        showToast('warning', '请输入参考答案');
        return false;
      }
    }

    return true;
  };

  // 添加题目
  const handleAddQuestion = () => {
    if (!validateForm()) return;

    const newQuestion: ExamQuestion = {
      question_id: Date.now(), // 临时 ID
      question_type: questionForm.question_type as ExamQuestion['question_type'],
      question_content: questionForm.question_content,
      options: questionForm.question_type === 'essay' ? null : questionForm.options,
      correct_answer: questionForm.correct_answer,
      difficulty: questionForm.difficulty as ExamQuestion['difficulty'],
      score: questionForm.score || 10,
    };

    setQuestions([...questions, newQuestion]);
    setQuestionForm(emptyQuestion);
    setIsAddingQuestion(false);
    showToast('success', '题目已添加');
  };

  // 编辑题目
  const handleEditQuestion = (index: number) => {
    const q = questions[index];
    setEditingIndex(index);
    setIsAddingQuestion(false);

    // 获取选项，判断题需要处理默认值
    let options = q.options as Record<string, string> || { A: '', B: '', C: '', D: '' };
    if (q.question_type === 'judgment') {
      options = {
        A: options.A || '正确',
        B: options.B || '错误',
        C: '',
        D: '',
      };
    }

    setQuestionForm({
      question_type: q.question_type as QuestionCreateInPaper['question_type'],
      question_content: q.question_content,
      options,
      correct_answer: q.correct_answer,
      difficulty: q.difficulty as QuestionCreateInPaper['difficulty'],
      score: q.score,
    });
  };

  // 保存编辑
  const handleSaveEdit = () => {
    if (editingIndex === null || !validateForm()) return;

    const updatedQuestions = [...questions];
    updatedQuestions[editingIndex] = {
      ...updatedQuestions[editingIndex],
      question_type: questionForm.question_type as ExamQuestion['question_type'],
      question_content: questionForm.question_content,
      options: questionForm.question_type === 'essay' ? null : questionForm.options,
      correct_answer: questionForm.correct_answer,
      difficulty: questionForm.difficulty as ExamQuestion['difficulty'],
      score: questionForm.score || 10,
    };
    setQuestions(updatedQuestions);
    setEditingIndex(null);
    setQuestionForm(emptyQuestion);
    showToast('success', '题目已更新');
  };

  // 删除题目
  const handleDeleteQuestion = async (index: number) => {
    const confirmed = await confirmDialog.confirm({
      title: '确认删除',
      message: '确定要删除这道题目吗？',
      confirmText: '确认删除',
      danger: true,
    });
    if (!confirmed) return;

    setQuestions(questions.filter((_, i) => i !== index));
    showToast('success', '题目已删除');
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setIsAddingQuestion(false);
    setEditingIndex(null);
    setQuestionForm(emptyQuestion);
  };

  // 保存试卷
  const handleSavePaper = async () => {
    setSaving(true);
    try {
      const questionsData = questions.map((q, index) => ({
        question_type: q.question_type,
        question_content: q.question_content,
        options: q.options,
        correct_answer: q.correct_answer,
        difficulty: q.difficulty,
        score: q.score,
        question_sort: index,
      }));

      const res = await api.updatePaperWithQuestions(
        paper.paper_id,
        {
          total_score: totalScore,
          pass_score: passScore,
          exam_duration: examDuration,
          questions: questionsData,
        },
        user?.user_id || 1
      );

      if (res.code === 200 && res.data) {
        showToast('success', '试卷保存成功');
        onSave(res.data);
      }
    } catch (error) {
      showToast('error', '保存试卷失败');
    } finally {
      setSaving(false);
    }
  };

  // 处理选项变更
  const handleOptionChange = (key: string, value: string) => {
    setQuestionForm({
      ...questionForm,
      options: {
        ...(questionForm.options as Record<string, string>),
        [key]: value,
      },
    });
  };

  // 处理正确答案选择（多选题）
  const handleMultipleAnswerToggle = (key: string) => {
    const current = questionForm.correct_answer.split('');
    if (current.includes(key)) {
      setQuestionForm({
        ...questionForm,
        correct_answer: current.filter(c => c !== key).sort().join(''),
      });
    } else {
      setQuestionForm({
        ...questionForm,
        correct_answer: [...current, key].sort().join(''),
      });
    }
  };

  // 渲染题目表单（用于编辑和添加）
  const renderQuestionForm = (isInline: boolean = false) => (
    <motion.div
      className={styles.questionForm}
      initial={isInline ? { opacity: 0, scale: 0.98 } : false}
      animate={isInline ? { opacity: 1, scale: 1 } : false}
      transition={{ duration: 0.2 }}
    >
      {/* 题目类型选择 */}
      <div className={styles.formGroup}>
        <label>题目类型</label>
        <div className={styles.typeSelector}>
          {QUESTION_TYPES.map(type => (
            <button
              key={type.value}
              type="button"
              className={`${styles.typeBtn} ${questionForm.question_type === type.value ? styles.active : ''}`}
              onClick={() => {
                const newType = type.value as any;
                const newForm = { ...questionForm, question_type: newType, correct_answer: 'A' };
                // 判断题默认选项
                if (newType === 'judgment') {
                  newForm.options = { A: '正确', B: '错误', C: '', D: '' };
                } else {
                  newForm.options = { A: '', B: '', C: '', D: '' };
                }
                setQuestionForm(newForm);
              }}
              style={questionForm.question_type === type.value ? { borderColor: type.color, backgroundColor: `${type.color}15` } : {}}
            >
              <type.icon size={16} style={{ color: type.color }} />
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 题目内容 */}
      <div className={styles.formGroup}>
        <label>题目内容 *</label>
        <textarea
          placeholder="请输入题目内容"
          value={questionForm.question_content}
          onChange={e => setQuestionForm({ ...questionForm, question_content: e.target.value })}
          rows={3}
          autoFocus
        />
      </div>

      {/* 选项 - 非简答题 */}
      {questionForm.question_type !== 'essay' && (
        <div className={styles.formGroup}>
          <label>选项</label>
          {questionForm.question_type === 'judgment' ? (
            // 判断题选项
            <div className={styles.judgmentOptions}>
              {['A', 'B'].map(key => {
                const defaultLabel = key === 'A' ? '正确' : '错误';
                const currentValue = (questionForm.options as Record<string, string>)?.[key] || defaultLabel;
                return (
                  <div key={key} className={styles.judgmentOption}>
                    <input
                      type="radio"
                      name="correct_judgment"
                      checked={questionForm.correct_answer === key}
                      onChange={() => setQuestionForm({ ...questionForm, correct_answer: key })}
                    />
                    <span className={styles.optionLabel}>{key}.</span>
                    <input
                      type="text"
                      placeholder={defaultLabel}
                      value={currentValue}
                      onChange={e => handleOptionChange(key, e.target.value)}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            // 单选/多选题选项
            <div className={styles.optionsGrid}>
              {['A', 'B', 'C', 'D', 'E', 'F'].map(key => (
                <div key={key} className={styles.optionInput}>
                  <input
                    type={questionForm.question_type === 'multiple' ? 'checkbox' : 'radio'}
                    name="correct_option"
                    checked={questionForm.correct_answer.includes(key)}
                    onChange={() => {
                      if (questionForm.question_type === 'multiple') {
                        handleMultipleAnswerToggle(key);
                      } else {
                        setQuestionForm({ ...questionForm, correct_answer: key });
                      }
                    }}
                  />
                  <span className={styles.optionLabel}>{key}.</span>
                  <input
                    type="text"
                    placeholder={`选项 ${key}`}
                    value={(questionForm.options as Record<string, string>)?.[key] || ''}
                    onChange={e => handleOptionChange(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}
          <p className={styles.hint}>
            {questionForm.question_type === 'multiple'
              ? '勾选选项前的复选框设置正确答案（可选多个）'
              : '选择选项前的单选框设置正确答案'}
          </p>
        </div>
      )}

      {/* 简答题参考答案 */}
      {questionForm.question_type === 'essay' && (
        <div className={styles.formGroup}>
          <label>参考答案 *</label>
          <textarea
            placeholder="请输入参考答案"
            value={questionForm.correct_answer}
            onChange={e => setQuestionForm({ ...questionForm, correct_answer: e.target.value })}
            rows={4}
          />
        </div>
      )}

      {/* 分值和难度 */}
      <div className={styles.formRow}>
        <div className={styles.formGroup}>
          <label>分值</label>
          <input
            type="number"
            min="1"
            max="100"
            value={questionForm.score}
            onChange={e => setQuestionForm({ ...questionForm, score: parseInt(e.target.value) || 10 })}
          />
        </div>
        <div className={styles.formGroup}>
          <label>难度</label>
          <select
            value={questionForm.difficulty}
            onChange={e => setQuestionForm({ ...questionForm, difficulty: e.target.value as any })}
          >
            {DIFFICULTY_OPTIONS.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className={styles.formActions}>
        <button type="button" className={styles.cancelBtn} onClick={handleCancelEdit}>
          取消
        </button>
        <button
          type="button"
          className={styles.confirmBtn}
          onClick={editingIndex !== null ? handleSaveEdit : handleAddQuestion}
        >
          {editingIndex !== null ? '保存修改' : '添加题目'}
        </button>
      </div>
    </motion.div>
  );

  // 渲染题目项
  const renderQuestionItem = (question: ExamQuestion, index: number) => {
    const typeConfig = getQuestionTypeConfig(question.question_type);
    const difficultyConfig = getDifficultyConfig(question.difficulty);

    // 如果正在编辑这道题目，显示表单
    if (editingIndex === index) {
      return (
        <motion.div
          key={`edit-${question.question_id}`}
          layout
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {renderQuestionForm(true)}
        </motion.div>
      );
    }

    return (
      <motion.div
        key={question.question_id}
        className={styles.questionItem}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
        layout
      >
        <div className={styles.questionHeader}>
          <div className={styles.questionNumber}>
            <GripVertical size={16} className={styles.dragHandle} />
            <span className={styles.numberBadge}>第 {index + 1} 题</span>
            <span
              className={styles.questionType}
              style={{ backgroundColor: `${typeConfig.color}15`, color: typeConfig.color }}
            >
              <typeConfig.icon size={12} />
              {typeConfig.label}
            </span>
          </div>
          <div className={styles.questionMeta}>
            <span className={styles.score}>{question.score} 分</span>
            <span className={styles.difficulty} style={{ color: difficultyConfig.color }}>
              {difficultyConfig.label}
            </span>
          </div>
        </div>

        <div className={styles.questionContent}>
          <p>{question.question_content}</p>
        </div>

        {question.options && question.question_type !== 'essay' && (
          <div className={styles.questionOptions}>
            {Object.entries(question.options as Record<string, string>)
              .filter(([, value]) => value?.trim())
              .map(([key, value]) => (
                <div
                  key={key}
                  className={`${styles.option} ${question.correct_answer.includes(key) ? styles.correct : ''}`}
                >
                  <span className={styles.optionKey}>{key}.</span>
                  <span className={styles.optionValue}>{value}</span>
                  {question.correct_answer.includes(key) && (
                    <Check size={14} className={styles.checkIcon} />
                  )}
                </div>
              ))}
          </div>
        )}

        {question.question_type === 'essay' && (
          <div className={styles.essayAnswer}>
            <span className={styles.answerLabel}>参考答案：</span>
            <p>{question.correct_answer}</p>
          </div>
        )}

        <div className={styles.questionActions}>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => moveQuestion(index, 'up')}
            disabled={index === 0}
            title="上移"
          >
            <ChevronUp size={16} />
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => moveQuestion(index, 'down')}
            disabled={index === questions.length - 1}
            title="下移"
          >
            <ChevronDown size={16} />
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={() => handleEditQuestion(index)}
            title="编辑"
          >
            <Edit3 size={16} />
          </button>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.danger}`}
            onClick={() => handleDeleteQuestion(index)}
            title="删除"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className={styles.editor}>
      {/* 编辑器头部 */}
      <div className={styles.editorHeader}>
        <div className={styles.headerInfo}>
          <h2>{paper.paper_name}</h2>
          <div className={styles.stats}>
            <span><HelpCircle size={14} /> {questions.length} 题</span>
            <span>总分: {totalScore} 分</span>
          </div>
        </div>
        <div className={styles.headerActions}>
          {/* 试卷设置 */}
          <div className={styles.paperSettings}>
            <div className={styles.settingItem}>
              <label>及格分</label>
              <input
                type="number"
                min="0"
                max="100"
                value={passScore}
                onChange={e => setPassScore(parseInt(e.target.value) || 60)}
              />
            </div>
            <div className={styles.settingItem}>
              <label>时长(分钟)</label>
              <input
                type="number"
                min="1"
                max="300"
                value={examDuration}
                onChange={e => setExamDuration(parseInt(e.target.value) || 60)}
              />
            </div>
          </div>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => {
              setIsAddingQuestion(true);
              setEditingIndex(null);
              setQuestionForm(emptyQuestion);
            }}
            disabled={isAddingQuestion || editingIndex !== null}
          >
            <Plus size={16} />
            添加题目
          </button>
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            取消
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSavePaper}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 size={16} className={styles.spinner} />
                保存中...
              </>
            ) : (
              <>
                <Save size={16} />
                保存试卷
              </>
            )}
          </button>
        </div>
      </div>

      {/* 题目列表 */}
      <div className={styles.questionsContainer} ref={containerRef}>
        {/* 添加题目表单 - 在顶部显示 */}
        <AnimatePresence>
          {isAddingQuestion && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              {renderQuestionForm(true)}
            </motion.div>
          )}
        </AnimatePresence>

        {/* 题目列表 */}
        <AnimatePresence>
          {questions.map((question, index) => renderQuestionItem(question, index))}
        </AnimatePresence>

        {/* 空状态提示 */}
        {questions.length === 0 && !isAddingQuestion && (
          <div className={styles.emptyState}>
            <HelpCircle size={48} />
            <p>暂无题目</p>
            <span>点击上方"添加题目"按钮开始出题</span>
          </div>
        )}
      </div>
    </div>
  );
}